import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, BookOpen, Loader2, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const GenerationScreen = ({ progress, statusMessage, onBackToDashboard }) => {
  const [displayProgress, setDisplayProgress] = useState(5);
  const [dots, setDots] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);

  // Animate dots for loading effect
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Smooth progress animation - start at 5% immediately for feedback
  useEffect(() => {
    const targetProgress = Math.max(progress, 5);
    if (targetProgress > displayProgress) {
      const timer = setTimeout(() => {
        setDisplayProgress((prev) => Math.min(prev + 1, targetProgress));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [progress, displayProgress]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  };

  // Estimate remaining time based on progress
  const getEstimatedTime = () => {
    if (displayProgress <= 5) return "~2 minutes";
    if (displayProgress < 20) return "~1m 45s";
    if (displayProgress < 40) return "~1m 30s";
    if (displayProgress < 50) return "~1m 15s";
    if (displayProgress < 60) return "~1 minute";
    if (displayProgress < 70) return "~45s";
    if (displayProgress < 80) return "~30s";
    if (displayProgress < 90) return "~20s";
    if (displayProgress < 95) return "~10s";
    return "Quelques secondes";
  };

  const getStatusMessage = () => {
    if (statusMessage) return statusMessage;
    if (displayProgress < 10) return "Connexion à l'IA en cours";
    if (displayProgress < 30) return "Analyse des sources";
    if (displayProgress < 60) return "Extraction des histoires";
    if (displayProgress < 90) return "Rédaction de l'autobiographie";
    return "Finalisation";
  };

  return (
    <div className="bg-background">
      <main className="min-h-[calc(100vh-var(--shell-h))] flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-lg"
        >
          <Card className="border-border shadow-lg overflow-hidden">
            {/* Animated top bar */}
            <div className="h-1 bg-muted overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  ease: "easeInOut",
                }}
                style={{ width: "50%" }}
              />
            </div>

            <CardContent className="pt-10 pb-10 px-8">
              {/* Icon animation */}
              <div className="flex justify-center mb-8">
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative"
                >
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-primary" />
                  </div>
                  {/* Spinning ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-primary/30 border-t-primary"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </motion.div>
              </div>

              {/* Title */}
              <h2 className="font-heading text-2xl font-semibold text-center mb-2">
                Génération en cours
              </h2>

              {/* Status message with animated dots */}
              <p
                className="text-center text-muted-foreground mb-2 h-6 flex items-center justify-center gap-1"
                data-testid="generation-status-text"
                aria-live="polite"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{getStatusMessage()}{dots}</span>
              </p>

              {/* Time info */}
              <div className="flex justify-center gap-6 text-sm mb-6">
                <div className="text-center">
                  <p className="text-muted-foreground">Écoulé</p>
                  <p className="font-medium text-foreground">{formatTime(elapsedTime)}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground">Restant estimé</p>
                  <p className="font-medium text-primary">{getEstimatedTime()}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-3">
                <Progress
                  value={displayProgress}
                  className="h-3"
                  data-testid="generation-progress"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Progression</span>
                  <span className="font-medium text-foreground">{displayProgress}%</span>
                </div>
              </div>

              {/* Steps indicator */}
              <div className="mt-8 space-y-3">
                <Step
                  label="Connexion à l'IA"
                  active={displayProgress > 0 && displayProgress < 10}
                  completed={displayProgress >= 10}
                />
                <Step
                  label="Analyse des sources"
                  active={displayProgress >= 10 && displayProgress < 40}
                  completed={displayProgress >= 40}
                />
                <Step
                  label="Extraction des histoires"
                  active={displayProgress >= 40 && displayProgress < 70}
                  completed={displayProgress >= 70}
                />
                <Step
                  label="Rédaction de l'autobiographie"
                  active={displayProgress >= 70 && displayProgress < 95}
                  completed={displayProgress >= 95}
                />
                <Step
                  label="Finition"
                  active={displayProgress >= 95 && displayProgress < 100}
                  completed={displayProgress >= 100}
                />
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Ce processus prend généralement <strong>1 à 2 minutes</strong>.<br />
            L'IA analyse vos sources en profondeur pour créer une autobiographie unique.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

const Step = ({ label, active, completed, timeEstimate }) => {
  return (
    <motion.div
      className="flex items-center gap-3"
      initial={false}
      animate={{
        opacity: completed || active ? 1 : 0.5,
      }}
    >
      <AnimatePresence mode="wait">
        {completed ? (
          <motion.div
            key="completed"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-5 h-5 rounded-full bg-primary flex items-center justify-center"
          >
            <CheckCircle2 className="w-4 h-4 text-primary-foreground" />
          </motion.div>
        ) : active ? (
          <motion.div
            key="active"
            className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center"
          >
            <motion.div
              className="w-2 h-2 rounded-full bg-primary"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </motion.div>
        ) : (
          <div
            key="pending"
            className="w-5 h-5 rounded-full border-2 border-muted"
          />
        )}
      </AnimatePresence>
      <span
        className={`text-sm transition-colors duration-300 ${
          completed
            ? "text-foreground font-medium"
            : active
            ? "text-foreground"
            : "text-muted-foreground"
        }`}
      >
        {label}
      </span>
      {completed && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="ml-auto"
        >
          <Sparkles className="w-4 h-4 text-primary" />
        </motion.div>
      )}
    </motion.div>
  );
};

export default GenerationScreen;
